import { gql } from '@apollo/client';
import { TRANSLATED_STRING_FIELDS } from '@assets/i18n/config';

const PORTFOLIO_REPORT_HOLDING_VALUE_FIELDS = `
  currency
  expectedValueCents
  expectedPercentage
  valueCents
  quantity
  pendingQuantity
`;

const PORTFOLIO_REPORT_HOLDING_FIELDS = `
  taxRank
  isCashEquivalent
  ${PORTFOLIO_REPORT_HOLDING_VALUE_FIELDS}
  originalValues {
    ${PORTFOLIO_REPORT_HOLDING_VALUE_FIELDS}
  }
  financialProduct {
    id
    ticker
    isCash
    currentPriceCents
    name
    taxRank
    currency
    originalCurrency
    originalCurrentPriceCents
    settlementDays
    translatedName { ${TRANSLATED_STRING_FIELDS} }
  }
  assetClass {
    id
    name
    translatedName { ${TRANSLATED_STRING_FIELDS} }
  }
  assetClassPercentage
  assetClassGroupPercentage
  # NOTE: internalExpectedValueCents is only populated by the backend on top-level
  # portfolioReport.holdings — not on subAccounts[].holdings. Always read it from
  # the top-level holding (portfolio.tsx iterates portfolioReport.holdings for this).
  internalExpectedValueCents
  customExpectedPercentage
`;

const PORTFOLIO_REPORT_SUB_ACCOUNT_CASH_FIELDS = `
  currency
  cashCents
  expectedCashCents
  cashAvailableForTradeCents
  marketValueCents
  pendingWithdrawCents
  pendingContributionCents
  pendingCashCents
  cashOnHoldToTradeCents
`;

const PORTFOLIO_REPORT_SUB_ACCOUNT_FIELDS = `
  subAccount {
    id
    state
    account {
      taxRank
      type
      user {
        firstName
        entityName
      }
    }
  }
  ${PORTFOLIO_REPORT_SUB_ACCOUNT_CASH_FIELDS}
  accountBaseCurrency
  originalValues {
    ${PORTFOLIO_REPORT_SUB_ACCOUNT_CASH_FIELDS}
  }
  holdings {
    ${PORTFOLIO_REPORT_HOLDING_FIELDS}
  }
`;

export const PORTFOLIO_REPORT_FIELDS = `
  id
  modelPortfolioType
  allocationMethodology
  object {
    ... on Goal {
      id
      name
      __typename
    }
    ... on SubAccount {
      id
      __typename
      account { id type user { id firstName lastName entityName } }
      goal { id name }
    }
    ... on ClientGroup {
      id
      clientGroupName: name
      __typename
    }
  }
  baseCurrency
  originalCurrencies
  fxRates {
    from
    to
    rate
  }
  modelPortfolio {
    id
    strategy
  }
  lastOptimizedBy {
    id
    createdAt
  }
  updatedAt
  refreshState
  subAccounts {
    ${PORTFOLIO_REPORT_SUB_ACCOUNT_FIELDS}
  }
  cashCents
  pendingCashCents
  expectedCashPercentage
  cashAssetClass {
    id
  }
  cashAssetClassGroupPercentage
  cashAssetClassPercentage
  cashAvailableForTradeCents
  expectedCashCents
  driftRebalanceRequired
  transferRebalanceRequired
  liquidateRebalanceRequired
  pendingSubTradeRequests
  unverifiedSubTradeRequests
  bulkTradeRunReasons {
    reason
    total
    unblockedSince
  }
  holdings {
    ${PORTFOLIO_REPORT_HOLDING_FIELDS}
  }
`;

const OPTIMAL_HOLDING_FIELDS = `
  index
  financialProduct
  expectedValueCents
  valueCents
  taxRank
`;

const OPTIMAL_PORTFOLIO_FIELDS = `
  id
  modelPortfolio
  modelPortfolioType
  expectedCashPercentage
  expectedCashCents
  subAccounts {
    index
    subAccount
    account
    user
    goal
    marketValueCents
    expectedCashCents
    cashCentsDeviation
    cashCentsToInvest
    taxable
    taxRank
    optimalHoldings {
      ${OPTIMAL_HOLDING_FIELDS}
    }
    currentHoldingAllocations
    currentHoldingQuantities
    preTradeHoldingAllocations
    preTradeHoldingPurchaseAllocations
  }
  optimalHoldings {
    ${OPTIMAL_HOLDING_FIELDS}
  }
  preTradeHoldings {
    index
    financialProduct
    moneyAllocatedCents
    diffValueCents
    diffPercentage
    expectedPercentage
    taxRank
    moneyAllocatedCentsRedemptions
    moneyAllocatedCentsPurchases
    diffPercentagePurchasesStage1
  }
  lastOptimizedBy
`;

export const SUB_TRADE_REQUEST_FIELDS = `
  id
  financialProduct {
    id
    ticker
    currentPriceCents
    settlementDays
    currency
    originalCurrency
    exchange
    translatedName { ${TRANSLATED_STRING_FIELDS} }
  }
  settlementDate
  state
  isSellAll
  moneyAllocatedCents
  currency
  type
  subAccount {
    id
    account {
      id
      type
      custodianAccountNumber
      user {
        id
        firstName
        entityName
      }
    }
  }
  portfolioReport
  preSplitMoneyAllocatedCents
  isProcessedForPair
  pair {
    id
    financialProduct {
      ticker
    }
  }
  bulkTradeRun {
    id
  }
  verifiedAt
  verifiedByOrganizationUser {
    firstName
    lastName
  }
  blockedUntil
  blockedUntilAccountActive
  blockedUntilSubAccountActive
`;

export const SUB_TRADE_REQUEST_SPLIT_FIELDS = `
  splits {
    ${SUB_TRADE_REQUEST_FIELDS}
  }
`;

export const FETCH_PORTFOLIO_REPORTS = gql`
  query fetchPortfolioReports($input: FetchPortfolioReportsInput!) {
    fetchPortfolioReports(input: $input) {
      portfolioReports {
        ${PORTFOLIO_REPORT_FIELDS}
      }
    }
  }
`;

export const REFRESH_PORTFOLIO_REPORT = gql`
  mutation refreshPortfolioReport($input: RefreshPortfolioReportInput!) {
    refreshPortfolioReport(input: $input) {
      portfolioReport {
        ${PORTFOLIO_REPORT_FIELDS}
      }
    }
  }
`;

export const SET_ALLOCATION_METHODOLOGY = gql`
  mutation setAllocationMethodology($input: SetAllocationMethodologyInput!) {
    setAllocationMethodology(input: $input) {
      portfolioReport {
        id
        allocationMethodology
        refreshState
      }
    }
  }
`;

export const SET_CUSTOM_EXPECTED_PERCENTAGES = gql`
  mutation setCustomExpectedPercentages($input: SetCustomExpectedPercentagesInput!) {
    setCustomExpectedPercentages(input: $input) {
      portfolioReport {
        id
        refreshState
      }
    }
  }
`;

export const FETCH_BULK_TRADES = gql`
  query fetchBulkTradeRequests($input: FetchBulkTradeRequestsInput!) {
    fetchBulkTradeRequests(input: $input) {
      bulkTradeRequests {
        id
        state
      }
      totalCount
    }
  }
`;

export const FETCH_SUB_TRADE_REQUESTS_QUERY = (isWithSplits = true) => `
  query fetchSubTradeRequests($input: FetchSubTradeRequestsInput!) {
    fetchSubTradeRequests(input: $input) {
      subTradeRequests {
        ${SUB_TRADE_REQUEST_FIELDS}
        ${isWithSplits ? SUB_TRADE_REQUEST_SPLIT_FIELDS : ''}
      }
      totalCount
    }
  }
`;

export const FETCH_SUB_TRADE_REQUESTS = (isWithSplits = true) => gql`
  ${FETCH_SUB_TRADE_REQUESTS_QUERY(isWithSplits)}
`;

export const FETCH_OPTIMAL_PORTFOLIO = gql`
  query fetchOptimalPortfolio($input: FetchOptimalPortfolioInput!) {
    fetchOptimalPortfolio(input: $input) {
      optimalPortfolio {
        ${OPTIMAL_PORTFOLIO_FIELDS}
      }
    }
  }
`;

export const CANCEL_SUB_TRADE_REQUESTS_BY_ID = gql`
  mutation cancelSubTradeRequestsById(
    $input: CancelSubTradeRequestsByIdInput!
  ) {
    cancelSubTradeRequestsById(input: $input)
  }
`;

export const FETCH_HOUSEHOLD_ATTACHED_MODEL = gql`
  query fetchHouseholdAttachedModel($clientGroupId: SafeString!) {
    fetchClientGroup(input: { clientGroupId: $clientGroupId }) {
      clientGroup {
        id: stringId
        tradingProcess
        organization {
          id
        }
        financialProduct {
          id
          theme {
            id
          }
        }
      }
    }
  }
`;

export const FETCH_ATTACHED_MODEL_THEME = gql`
  query fetchAttachedModelTheme($input: FetchFinancialProductInput!) {
    fetchFinancialProduct(input: $input) {
      financialProduct {
        id
        theme {
          id
        }
      }
    }
  }
`;

export const RUN_PORTFOLIO_OPTIMIZER_V2 = gql`
  mutation runPortfolioOptimizerV2($input: RunPortfolioOptimizerV2Input!) {
    runPortfolioOptimizerV2(input: $input) {
      optimizerRun {
        id
        state
      }
    }
  }
`;

export const FETCH_OPTIMIZER_RUN_V2 = gql`
  query fetchOptimizerRunV2($input: FetchOptimizerRunV2Input!) {
    fetchOptimizerRunV2(input: $input) {
      optimizerRun {
        id
        state
      }
    }
  }
`;
